import { BaseModel } from 'projects/core/src/lib/models/base.model';
import { CandidateModel } from './candidate.model';
import { ObserverModel } from './observer.model';
import { ExerciseModel } from './exercise.model';
import { AcStatusSettingsExerciseType, AcStatusTimingsType, ActiveTiming, SlotStatus, TimerType } from './acstatus/ac-status.type';
import { AcModel } from './ac.model';
import { SlotModel } from './slot.model';

export class GroupModel extends BaseModel {

  name: string;
  hash: string;
  moderator: string;

  // relations
  exercise: ExerciseModel = null;
  candidates: Array<CandidateModel> = [];
  observers: Array<ObserverModel> = [];

  candidateObservers: { [key: string]: Array<ObserverModel> } = {};

  deserialize(input: any): this {
    Object.assign(this, input);
    return this;
  }

  /**
   * Checks, if a user with a specific hash is group moderator
   * @param hash string
   * @returns boolean
   */
  public isGroupModerator(hash: string): boolean {
    return this.moderator === hash;
  }

  /**
   * Checks, if a user with a specific hash is in group
   * @param hash string
   * @returns boolean
   */
  public isInGroup(hash: string): boolean {
    return this.observers.find(observer => observer.hashed_id === hash) !== undefined ||
      this.candidates.find(candidate => candidate.hashed_id === hash) !== undefined;
  }

  /**
   * Gets the linked observers for a candidate (hash)
   * @param hash string
   * @returns Array<ObserverModel>
   */
  public getLinkedObservers(hash: string): Array<ObserverModel> {
    return this.candidateObservers[hash] || [];
  }

  /**
   * Checks if an observer is linked to a candidate (hash)
   * @param candidateHash string
   * @param observerHash string
   * @returns boolean
   */
  public isLinkedToCandidate(candidateHash: string, observerHash: string): boolean {
    const observerHashes = this.getLinkedObservers(candidateHash).map(observer => observer.hashed_id);
    return observerHashes.includes(observerHash);
  }

  /**
   * Sets candidates and observers in this group and links candidate and observers togther
   * @param settings AcStatusSettingsExerciseType
   * @param ac AcModel
   */
  public setParticipants(settings: AcStatusSettingsExerciseType, ac: AcModel): void {
    // put candidates in
    const candidateHashs = [];
    Object.keys(settings.candidates).forEach((candidateHash: string) => {
      if (settings.candidates[candidateHash] && settings.candidates[candidateHash].group === this.hash) {
        candidateHashs.push(candidateHash);
      }
    });
    this.candidates = ac?.candidates?.
      filter(candidate => (ac.isSingle() || candidateHashs.includes(candidate.hashed_id))).
      sort((a: CandidateModel, b: CandidateModel) => {
        const seqA = settings.candidates[a.hashed_id]?.sequence || 0;
        const seqB = settings.candidates[b.hashed_id]?.sequence || 0;
        return seqA - seqB;
      });
    // put observers in
    const observerHashs = [];
    Object.keys(settings.observers).forEach((observerHash: string) => {
      if (settings.observers[observerHash] && settings.observers[observerHash].group === this.hash) {
        observerHashs.push(observerHash);
      }
    });
    this.observers = ac?.observers?.filter(observer => (ac.isSingle() || observerHashs.includes(observer.hashed_id)));
    // link candidates and observers
    this.candidateObservers = {};
    if (this.exercise.isTeam() && settings.has_matrix === true) {
      // team exercise with matrix
      this.candidates.forEach((candidate: CandidateModel) => {
        const observerHashes = settings.candidates[candidate.hashed_id].observers || [];
        this.candidateObservers[candidate.hashed_id] = this.observers.filter((observer: ObserverModel) => {
          return observerHashes.includes(observer.hashed_id);
        });
      });
    } else {
      // individual exercise or team exercise WITHOUT matrix
      this.candidates.forEach((candidate: CandidateModel) => {
        this.candidateObservers[candidate.hashed_id] = this.observers;
      });
    }
  }

  public setBusyFlags(timings: Array<ActiveTiming>, ac: AcModel): void {
    timings.forEach((timing: ActiveTiming) => {
      if (timing.hash === this.hash) {
        // group timing
        this.candidates.forEach((candidate: CandidateModel) => {
          this.setCandidateAsBusy(candidate, timing.slot, timing.timing, ac);
        });
      } else {
        // candidate timing
        const candidate = this.candidates.find(actual => actual.hashed_id === timing.hash) || null;
        if (candidate !== null) {
          this.setCandidateAsBusy(candidate, timing.slot, timing.timing, ac);
        }
      }
    });
  }

  private setCandidateAsBusy(candidate: CandidateModel, slot: SlotModel, timing: AcStatusTimingsType, ac: AcModel): void {
    if (
      slot.for_candidate === true &&
      (
        timing?.exercise?.status === SlotStatus.Running ||
        timing?.exercise?.status === SlotStatus.Paused
      )
    ) {
      candidate.busy = {
        slot,
        group: this,
        mode: TimerType.Exercise
      };
    }
    if (
      slot.for_observer === true ||
      timing?.rating?.status === SlotStatus.Running ||
      timing?.rating?.status === SlotStatus.Paused
    ) {
      const observers = this.candidateObservers[candidate.hashed_id] || [];
      const mode = (timing?.rating?.status === SlotStatus.Running || timing?.rating?.status === SlotStatus.Paused) ? TimerType.Rating : TimerType.Exercise;
      observers.forEach((observer: ObserverModel) => {
        observer.busy = {
          slot,
          group: this,
          candidate,
          mode
        };
      });
    }
  }

}
